import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';

/**
 * Faceted article listing.
 *
 * @typedef {object} ArticleListingRefs
 * @property {HTMLElement} grid
 * @property {HTMLInputElement} [input]
 * @property {HTMLButtonElement} [clear]
 * @property {HTMLButtonElement} [filterToggle]
 * @property {HTMLElement} resultsLabel
 * @property {HTMLElement} count
 * @property {HTMLElement} loading
 * @property {HTMLElement} empty
 * @property {HTMLElement} error
 * @property {HTMLButtonElement} [loadMore]
 * @property {HTMLElement} [pagination]
 * @property {HTMLElement} [pageInfo]
 * @property {HTMLElement} [prev]
 * @property {HTMLElement} [next]
 *
 * @extends Component<ArticleListingRefs>
 */
class ArticleListing extends Component {
  requiredRefs = ['grid', 'resultsLabel', 'count', 'loading', 'empty', 'error'];

  /** @type {Record<string, any>} */
  #config = {
    token: '',
    apiVersion: '2026-07',
    blogTitle: '',
    hasBlog: false,
    lockedTag: '',
    lockedGroup: '',
    searchUrl: '',
    typeLabel: 'Content Type',
    topicLabel: 'Content Topic',
    allLabel: 'All',
    pageSize: 12,
    navStyle: 'load_more',
    total: 0,
  };

  #initialHTML = '';

  /** @type {AbortController | null} */
  #controller = null;
  #requestId = 0;

  /** @type {string | null} */
  #cursor = null;
  #hasNext = false;
  #seenIds = new Set();

  #page = 1;
  #initialPage = 1;
  #afters = [null];

  #activeTotal = null;
  #activeTotalMore = false;

  #suppressUrlSync = false;
  /** @type {(() => void) | null} */
  #onPopState = null;

  /**
   * @param {string} key @param {Record<string, string|number>} [params]
   */
  #t(key, params = {}) {
    let str = this.#config.i18n?.[key] ?? '';
    for (const [k, v] of Object.entries(params)) str = str.replace(`[${k}]`, String(v));
    return str;
  }

  connectedCallback() {
    super.connectedCallback();

    const configEl = this.querySelector('script[ref="config"]');
    if (configEl?.textContent) {
      try {
        this.#config = { ...this.#config, ...JSON.parse(configEl.textContent) };
      } catch {
        // keep defaults
      }
    }

    this.#initialHTML = this.refs.grid.innerHTML;
    this.#seedSeenIds();
    this.#hasNext = this.#config.total > this.#config.pageSize;

    if (this.#config.navStyle === 'pagination') {
      // Seed current page from ?page= so JS navigation continues from the
      // Liquid-paginated server render (e.g. deep-link to /blogs/kc?page=3).
      this.#page = this.#pageFromUrl();
      this.#initialPage = this.#page;
      // Trust the server-rendered pagination controls for hasNext: if the
      // "next" anchor isn't aria-disabled, there's another page available.
      if (this.refs.next) {
        this.#hasNext = this.refs.next.getAttribute('aria-disabled') !== 'true';
      }

      this.#onPopState = () => this.#handlePopState();
      window.addEventListener('popstate', this.#onPopState);
    }

    const hadServerFacets = Boolean(this.querySelector('input[data-facet]'));
    const facetsReady = Promise.resolve(this.#ensureFacets());

    if (hadServerFacets) {
      this.#initFromState();
    } else {
      facetsReady.then(() => this.#initFromState());
    }
  }

  #initFromState() {
    const urlTerm = new URLSearchParams(window.location.search).get('q');
    if (urlTerm && this.refs.input) {
      this.refs.input.value = urlTerm;
    } else if (this.#isSessionContinuation()) {
      this.#restorePersistedState();
    } else {
      this.#clearPersistedState();
      if (this.refs.input) this.refs.input.value = '';
      this.#setGroupChecked('type', false);
      this.#setGroupChecked('category', false);
      if (this.#allInput) this.#allInput.checked = false;
    }
    this.#updateClearButton();

    if (this.#isActive(this.#state)) {
      this.#applyState({ resetPage: false, save: false });
    } else if (this.#config.hasBlog && !this.#config.lockedTag) {
      this.#updateNav(this.refs.grid.children.length);
    } else {
      this.#restoreDefault();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#debouncedSearch.cancel();
    this.#controller?.abort();
    if (this.#onPopState) {
      window.removeEventListener('popstate', this.#onPopState);
      this.#onPopState = null;
    }
  }

  /* ---------- Event handlers (declarative on: attributes) ---------- */

  handleInput = () => {
    this.#updateClearButton();
    if (this.#config.searchUrl) return;
    this.#debouncedSearch();
  };

  /** Enter key in the search box. @param {KeyboardEvent} event */
  handleSearchKey = (event) => {
    if (event.key !== 'Enter') return;
    const term = this.refs.input?.value.trim() ?? '';

    if (this.#config.searchUrl) {
      event.preventDefault();
      const url = new URL(this.#config.searchUrl, window.location.origin);
      if (term) url.searchParams.set('q', term);
      window.location.assign(url.toString());
      return;
    }
    event.preventDefault();
    this.#debouncedSearch.cancel();
    this.#applyState();
  };

  handleFacet = (/** @type {Event} */ event) => {
    // The `on:change` binding lives on the facets container, so Horizon proxies
    // event.target to that container. Use composedPath to reach the actual input.
    const path = event.composedPath?.() ?? [];
    const target = /** @type {HTMLInputElement | undefined} */ (
      path.find((node) => node instanceof HTMLInputElement)
    );
    if (!target || target.dataset.facet === undefined) return;

    if (target.dataset.all !== undefined) {
      if (target.checked) this.#setGroupChecked('type', false, target);
    } else if (target.dataset.facet === 'type' && target.checked) {
      const all = this.#allInput;
      if (all) all.checked = false;
    }
    this.#applyState();
  };

  clearSearch = () => {
    if (this.refs.input) this.refs.input.value = '';
    this.#updateClearButton();
    if (this.#config.searchUrl) return;
    this.#applyState();
  };

  clearAll = () => {
    if (this.refs.input) this.refs.input.value = '';
    this.#setGroupChecked('type', false);
    this.#setGroupChecked('category', false);
    if (this.#allInput) this.#allInput.checked = false;
    this.#updateClearButton();
    this.#applyState();
  };

  retry = () => this.#applyState();

  loadMore = () => this.#fetchAppend();

  /** @param {Event} [event] */
  prevPage = (event) => {
    event?.preventDefault?.();
    if (this.#page > 1) {
      this.#goToPage(this.#page - 1);
      this.#scrollIntoView();
    }
  };

  /** @param {Event} [event] */
  nextPage = (event) => {
    event?.preventDefault?.();
    if (this.#hasNext) {
      this.#goToPage(this.#page + 1);
      this.#scrollIntoView();
    }
  };

  #scrollIntoView() {
    this.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'start',
    });
  }

  toggleDesktopFilters = () => {
    const closing = this.classList.contains('article-listing--filters-closing');
    const open = this.classList.contains('article-listing--filters-open') && !closing;
    this.refs.filterToggle?.setAttribute('aria-expanded', String(!open));

    if (!open) {
      this.classList.remove('article-listing--filters-closing');
      this.classList.add('article-listing--filters-open');
      return;
    }

    const sidebar = this.querySelector('.article-listing__filters-wrap');
    if (!sidebar || prefersReducedMotion()) {
      this.classList.remove('article-listing--filters-open');
      return;
    }

    // Keep the sidebar laid out until its slide-out finishes, then drop the open state.
    this.classList.add('article-listing--filters-closing');
    /** @param {AnimationEvent} event */
    const onEnd = (event) => {
      if (event.target !== sidebar) return;
      sidebar.removeEventListener('animationend', onEnd);
      if (!this.classList.contains('article-listing--filters-closing')) return;
      this.classList.remove('article-listing--filters-closing', 'article-listing--filters-open');
    };
    sidebar.addEventListener('animationend', onEnd);
  };

  /* ---------- State ---------- */

  #debouncedSearch = debounce(() => this.#applyState(), 300);

  /** @returns {HTMLInputElement[]} */
  get #facetInputs() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[data-facet]')]);
  }

  get #allInput() {
    return /** @type {HTMLInputElement | undefined} */ (this.querySelector('input[data-all]'));
  }

  /** @returns {{ term: string, types: string[], categories: string[] }} */
  get #state() {
    const term = this.refs.input?.value.trim() ?? '';
    const types = [];
    const categories = [];
    for (const input of this.#facetInputs) {
      if (!input.checked || input.dataset.all !== undefined) continue;
      if (input.dataset.facet === 'type') types.push(input.value);
      else if (input.dataset.facet === 'category') categories.push(input.value);
    }
    return { term, types, categories };
  }

  /** @param {{ term: string, types: string[], categories: string[] }} state */
  #isActive(state) {
    return Boolean(state.term) || state.types.length > 0 || state.categories.length > 0;
  }

  /** @param {{ resetPage?: boolean, save?: boolean }} [options] */
  #applyState({ resetPage = true, save = true } = {}) {
    const state = this.#state;
    this.#updateHeading(state);

    if (save) this.#persistState(state);

    if (!this.#isActive(state)) {
      this.#restoreDefault();
      return;
    }

    this.#runQuery({ resetPage });
  }

  /** @param {{ resetPage?: boolean }} [options] */
  #runQuery({ resetPage = true } = {}) {
    this.#activeTotal = null;
    if (this.#config.navStyle === 'pagination') {
      if (resetPage) {
        this.#afters = [null];
        this.#page = 1;
      }
      this.#goToPage(this.#page);
    } else {
      this.#cursor = null;
      this.#seenIds.clear();
      this.#fetchFresh();
    }
    // Resolve the total for the heading, independent of what's paged in.
    this.#fetchCount();
  }

  get #storageKey() {
    return `article-listing:${this.id || this.#config.blogTitle || 'default'}`;
  }

  /** @param {{ term: string, types: string[], categories: string[] }} state */
  #persistState(state) {
    try {
      if (this.#isActive(state)) {
        sessionStorage.setItem(this.#storageKey, JSON.stringify(state));
      } else {
        sessionStorage.removeItem(this.#storageKey);
      }
    } catch {
      // sessionStorage may be unavailable (private mode, quota); persistence is best-effort.
    }
  }

  #clearPersistedState() {
    try {
      sessionStorage.removeItem(this.#storageKey);
    } catch {
      // best-effort
    }
  }

  #isSessionContinuation() {
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      const type = nav && /** @type {PerformanceNavigationTiming} */ (nav).type;
      return type === 'reload' || type === 'back_forward';
    } catch {
      return false;
    }
  }

  /** @returns {{ term?: string, types?: string[], categories?: string[] } | null} */
  #loadPersistedState() {
    try {
      const raw = sessionStorage.getItem(this.#storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  #restorePersistedState() {
    const stored = this.#loadPersistedState();
    if (!stored) return;

    if (stored.term && this.refs.input) {
      this.refs.input.value = stored.term;
    }

    const types = new Set(stored.types ?? []);
    const categories = new Set(stored.categories ?? []);
    if (types.size > 0 || categories.size > 0) {
      for (const input of this.#facetInputs) {
        if (input.dataset.all !== undefined) continue;
        const bag = input.dataset.facet === 'type' ? types : categories;
        if (bag.has(input.value)) input.checked = true;
      }
    }
  }

  #restoreDefault() {
    if (!this.#config.hasBlog || this.#config.lockedTag) {
      this.#runQuery();
      return;
    }

    this.#controller?.abort();
    this.#requestId++;
    this.refs.grid.innerHTML = this.#initialHTML;
    this.#seedSeenIds();
    this.#cursor = null;
    // initialHTML matches whichever page Liquid rendered at boot — keep the
    // shown articles and the URL in sync with that page.
    this.#page = this.#initialPage;
    this.#afters = [null];
    this.#activeTotal = null;
    this.#hasNext = this.#config.total > this.#config.pageSize * this.#page;
    this.#hide(this.refs.loading, this.refs.empty, this.refs.error);
    this.refs.grid.hidden = false;
    this.#updateNav(this.refs.grid.children.length);
    this.#syncUrl();
  }

  async #fetchFresh() {
    const requestId = this.#newRequest();
    if (!requestId) return;
    this.#setLoading(true);

    try {
      const connection = await this.#query(this.#afters[0], requestId);
      if (!connection) return;

      this.#cursor = connection.pageInfo.endCursor;
      this.#hasNext = connection.pageInfo.hasNextPage;
      this.#seenIds.clear();
      this.refs.grid.innerHTML = '';
      const added = this.#appendCards(connection.edges);

      this.#setLoading(false);
      if (added === 0) this.#showEmpty(this.#state.term);
      else this.#hide(this.refs.empty);
      this.#updateNav(added);
    } catch (error) {
      this.#handleError(error, requestId);
    }
  }

  async #fetchCount() {
    if (!this.#hasStorefront()) return;
    const requestId = this.#requestId;

    try {
      const json = await this.#graphql(
        COUNT_QUERY,
        { query: this.#buildSearchQuery(), first: 250, after: null },
        this.#controller?.signal
      );
      if (requestId !== this.#requestId) return; // superseded
      if (json.errors) return;

      const connection = json.data.articles;
      this.#activeTotal = connection.nodes.length;
      this.#activeTotalMore = connection.pageInfo.hasNextPage;
      this.#updateNav(this.refs.grid.children.length);
    } catch {
      // Count is best-effort; on failure the heading falls back to the number currently shown.
    }
  }

  async #ensureFacets() {
    if (this.querySelector('input[data-facet]')) return; // server already rendered them
    if (!this.querySelector('.article-listing__facets')) return;
    if (!this.#hasStorefront()) return;

    const query = this.#buildSearchQuery({ withState: false });
    const types = new Set();
    const categories = new Set();

    try {
      let after = null;
      let hasNext = true;
      let guard = 0;

      while (hasNext && guard < 20) {
        guard++;
        const json = await this.#graphql(TAGS_QUERY, { query, first: 250, after });
        if (json.errors) return;

        const connection = json.data.articles;
        for (const node of connection.nodes) {
          for (const tag of node.tags ?? []) {
            if (tag.startsWith('type:')) types.add(tag.slice(5).trim());
            else if (tag.startsWith('category:')) categories.add(tag.slice(9).trim());
          }
        }
        hasNext = connection.pageInfo.hasNextPage;
        after = connection.pageInfo.endCursor;
      }

      this.#renderFacets([...types].sort(), [...categories].sort());
    } catch {
      /* Filters are best-effort; leave them absent on failure. */
    }
  }

  /** @param {string[]} types @param {string[]} categories */
  #renderFacets(types, categories) {
    const { lockedGroup, typeLabel, topicLabel, allLabel } = this.#config;
    const facetsEl = this.querySelector('.article-listing__facets');
    if (!facetsEl) return;

    const fragment = document.createDocumentFragment();

    const showType = lockedGroup !== 'type' && types.length > 0;
    const showCategory = lockedGroup !== 'category' && categories.length > 0;

    if (showType) {
      fragment.append(this.#facetGroup(typeLabel, 'type', types, allLabel));
    }

    if (showType && showCategory) {
      const divider = el('hr', 'article-listing__facet-divider');
      divider.setAttribute('aria-hidden', 'true');
      fragment.append(divider);
    }

    if (showCategory) {
      fragment.append(this.#facetGroup(topicLabel, 'category', categories, ''));
    }

    facetsEl.replaceChildren(fragment);
  }

  /** @param {string} label @param {'type'|'category'} facet @param {string[]} values @param {string} allLabel */
  #facetGroup(label, facet, values, allLabel) {
    const fieldset = el('fieldset', 'article-listing__facet');
    fieldset.append(el('legend', 'article-listing__facet-title', label));
    if (allLabel) fieldset.append(this.#facetOption(facet, '', allLabel, true));
    for (const value of values) fieldset.append(this.#facetOption(facet, value, value, false));
    return fieldset;
  }

  /** @param {'type'|'category'} facet @param {string} value @param {string} label @param {boolean} isAll */
  #facetOption(facet, value, label, isAll) {
    const wrap = el('label', 'article-listing__option');
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.dataset.facet = facet;
    if (isAll) {
      input.dataset.all = '';
    } else {
      input.value = value;
    }
    wrap.append(input, el('span', '', label));
    return wrap;
  }

  async #fetchAppend() {
    if (!this.refs.loadMore) return;
    const requestId = this.#newRequest();
    if (!requestId) return;

    this.refs.loadMore.disabled = true;
    this.refs.loadMore.setAttribute('aria-busy', 'true');

    try {
      let added = 0;
      do {
        const connection = await this.#query(this.#cursor, requestId);
        if (!connection) return;
        this.#cursor = connection.pageInfo.endCursor;
        this.#hasNext = connection.pageInfo.hasNextPage;
        added = this.#appendCards(connection.edges);
      } while (added === 0 && this.#hasNext);

      this.#updateNav(this.refs.grid.children.length);
    } catch (error) {
      if (!(this.#controller?.signal.aborted || requestId !== this.#requestId)) {
        console.error('[article-listing]', error);
      }
    } finally {
      if (requestId === this.#requestId && this.refs.loadMore) {
        this.refs.loadMore.disabled = false;
        this.refs.loadMore.removeAttribute('aria-busy');
      }
    }
  }

  /* ---------- Pagination mode ---------- */

  /** @param {number} page */
  async #goToPage(page) {
    const requestId = this.#newRequest();
    if (!requestId) return;
    this.#setLoading(true);

    try {
      for (let k = 1; k < page; k++) {
        if (this.#afters[k] !== undefined) continue;
        const bootstrap = await this.#query(this.#afters[k - 1], requestId);
        if (!bootstrap) return;
        this.#afters[k] = bootstrap.pageInfo.endCursor;
      }

      const connection = await this.#query(this.#afters[page - 1], requestId);
      if (!connection) return;

      this.#afters[page] = connection.pageInfo.endCursor;
      this.#hasNext = connection.pageInfo.hasNextPage;
      this.#page = page;

      this.refs.grid.innerHTML = '';
      this.#seenIds.clear();
      const added = this.#appendCards(connection.edges);

      this.#setLoading(false);
      if (added === 0) this.#showEmpty(this.#state.term);
      else this.#hide(this.refs.empty);
      this.#updateNav(added);
      this.#syncUrl();
    } catch (error) {
      if (this.#controller?.signal.aborted || requestId !== this.#requestId) return;
      console.error('[article-listing]', error);
      this.#setLoading(false);
      this.#updateNav(this.refs.grid.children.length);
    }
  }

  /** Read the current page from ?page= (min 1). */
  #pageFromUrl() {
    const raw = new URLSearchParams(window.location.search).get('page');
    const parsed = parseInt(raw ?? '1', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  /** Return the URL for a specific page, preserving other params. @param {number} page */
  #urlForPage(page) {
    const url = new URL(window.location.href);
    if (page > 1) url.searchParams.set('page', String(page));
    else url.searchParams.delete('page');
    return url;
  }

  /** Push ?page= into the URL to reflect the current JS-driven page. */
  #syncUrl() {
    if (this.#config.navStyle !== 'pagination') return;
    if (this.#suppressUrlSync) return;
    const url = this.#urlForPage(this.#page);
    if (url.toString() !== window.location.href) {
      window.history.pushState({ page: this.#page }, '', url);
    }
  }

  #handlePopState() {
    if (this.#config.navStyle !== 'pagination') return;
    const target = this.#pageFromUrl();
    if (target === this.#page) return;
    this.#suppressUrlSync = true;
    Promise.resolve(this.#goToPage(target)).finally(() => {
      this.#suppressUrlSync = false;
    });
  }

  #newRequest() {
    if (!this.#hasStorefront()) {
      console.warn('[article-listing] Missing Storefront token or shop domain.');
      this.#showError();
      return 0;
    }
    this.#controller?.abort();
    this.#controller = new AbortController();
    if (this.refs.loadMore) {
      this.refs.loadMore.disabled = false;
      this.refs.loadMore.removeAttribute('aria-busy');
    }
    return ++this.#requestId;
  }

  /** @param {string | null | undefined} after @param {number} requestId */
  async #query(after, requestId) {
    const json = await this.#graphql(
      ARTICLES_QUERY,
      { query: this.#buildSearchQuery(), first: this.#config.pageSize, after: after ?? null },
      this.#controller?.signal
    );
    if (requestId !== this.#requestId) return null; // superseded
    if (json.errors) throw new Error(JSON.stringify(json.errors));
    return json.data.articles;
  }

  /** Storefront token + shop domain are required for every API call. */
  #hasStorefront() {
    return Boolean(this.#config.token && window.Shopify?.shop);
  }

  /**
   * @param {string} query @param {Record<string, unknown>} variables
   * @param {AbortSignal} [signal]
   */
  async #graphql(query, variables, signal) {
    const { token, apiVersion } = this.#config;
    const response = await fetch(`https://${window.Shopify.shop}/api/${apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query, variables }),
      signal,
    });
    return response.json();
  }

  /** @param {{ withState?: boolean }} [options] */
  #buildSearchQuery({ withState = true } = {}) {
    const { blogTitle, lockedTag } = this.#config;
    return buildSearchQuery({ blogTitle, lockedTag, ...(withState ? this.#state : {}) });
  }

  /** @param {unknown} error @param {number} requestId */
  #handleError(error, requestId) {
    if (this.#controller?.signal.aborted || requestId !== this.#requestId) return;
    console.error('[article-listing]', error);
    this.#showError();
  }

  /** @param {any[]} edges @returns {number} count of newly added cards */
  #appendCards(edges) {
    const fragment = document.createDocumentFragment();
    let added = 0;
    for (const { node } of edges) {
      const id = numericId(node.id);
      if (this.#seenIds.has(id)) continue;
      this.#seenIds.add(id);
      fragment.append(this.#renderCard(node));
      added++;
    }
    this.refs.grid.append(fragment);
    return added;
  }

  /**
   * @param {any} node - Article node from the Storefront API.
   * @returns {HTMLLIElement}
   */
  #renderCard(node) {
    const category = categoryFromNode(node) || node.blog?.title || '';
    const readTime = readTimeFromContent(node.content);
    const url = `/blogs/${node.blog.handle}/${node.handle}`;

    const li = el('li', 'article-listing-card');
    li.dataset.articleId = numericId(node.id);

    const media = el('div', 'article-listing-card__media');
    if (node.image?.url) {
      const img = document.createElement('img');
      const widths = [208, 282, 416, 564, 720, 900, 1110, 1440];
      img.src = sizedImage(node.image.url, 1440);
      img.srcset = widths.map((w) => `${sizedImage(node.image.url, w)} ${w}w`).join(', ');
      img.sizes = '(min-width: 1024px) min(20.3vw, 282px), (min-width: 750px) 38.7vw, 74vw';
      img.alt = node.image.altText ?? '';
      img.loading = 'lazy';
      media.append(img);
    } else {
      const placeholder = el('span', 'article-listing-card__media-placeholder');
      placeholder.setAttribute('aria-hidden', 'true');
      media.append(placeholder);
    }
    li.append(media);

    const body = el('div', 'article-listing-card__body');

    const meta = el('div', 'article-meta article-listing-card__meta text-body-secondary-s');
    if (category) meta.append(el('span', 'article-meta__category', category));
    const readtimeEl = el('span', 'article-meta__read-time');
    readtimeEl.append(el('span', 'visually-hidden', this.#t('readTimeLabel')));
    readtimeEl.append(document.createTextNode(this.#t('readTime', { minutes: readTime })));
    meta.append(readtimeEl);
    body.append(meta);

    const title = el('h3', 'article-listing-card__title text-heading-secondary-xs text-bold');
    const link = document.createElement('a');
    link.className = 'article-listing-card__link';
    link.href = url;
    link.textContent = node.title;
    title.append(link);
    body.append(title);

    const excerpt = truncateChars(stripHtml(node.excerpt || node.content || ''), 114);
    if (excerpt)
      body.append(
        el('p', 'article-listing-card__excerpt text-body-secondary-l text-medium', excerpt)
      );

    const more = el(
      'span',
      'article-listing-card__more text-label-s text-regular',
      this.#t('readMore')
    );
    more.setAttribute('aria-hidden', 'true');
    body.append(more);

    li.append(body);
    return li;
  }

  /** @param {{ term: string, types: string[], categories: string[] }} state */
  #updateHeading(state) {
    this.classList.toggle('article-listing--searching', Boolean(state.term));
    this.refs.resultsLabel.hidden = !state.term;
    this.#syncLabels();
  }

  /**
   * Which counter drives the totals shown in the UI: the live API count for
   * active searches/filters/tag pages, or the server-rendered blog count for
   * the default browse state.
   */
  get #usesApiTotal() {
    return this.#isActive(this.#state) || !this.#config.hasBlog || Boolean(this.#config.lockedTag);
  }

  #syncLabels() {
    const term = this.refs.input?.value.trim() ?? '';
    const shown = this.refs.grid.children.length;
    const fromApi = this.#usesApiTotal;
    const count = fromApi ? (this.#activeTotal ?? shown) : this.#config.total || shown;
    const countStr = fromApi && this.#activeTotalMore ? `${count}+` : `${count}`;

    if (term) {
      this.refs.resultsLabel.textContent = this.#t('resultsHeading', {
        count: countStr,
        query: term,
      });
    }

    this.refs.count.textContent = this.#t('articleCount', { count: countStr });

    const viewResultsBtn = this.querySelector('[data-view-results]');
    if (viewResultsBtn) {
      viewResultsBtn.textContent = `${this.#t('viewResults')} (${countStr})`;
    }
  }

  /** Update whichever navigation control is active. @param {number} countOnPage */
  #updateNav(countOnPage) {
    if (this.#config.navStyle === 'pagination') {
      this.#renderPagination(countOnPage);
    } else if (this.refs.loadMore) {
      this.refs.loadMore.hidden = !this.#hasNext;
    }
    this.#syncLabels();
  }

  /** @param {number} countOnPage */
  #renderPagination(countOnPage) {
    const { pagination, pageInfo, prev, next } = this.refs;
    if (!pagination || !pageInfo || !prev || !next) return;

    pagination.hidden = false;
    const { pageSize } = this.#config;
    const total = this.#usesApiTotal ? this.#activeTotal : this.#config.total;

    if (countOnPage === 0) {
      pageInfo.textContent = '';
    } else if (total) {
      const start = (this.#page - 1) * pageSize + 1;
      const end = start + countOnPage - 1;
      const totalStr =
        this.#activeTotalMore && total === this.#activeTotal ? `${total}+` : `${total}`;
      pageInfo.textContent = this.#t('paginationCount', { start, end, total: totalStr });
    } else {
      pageInfo.textContent = this.#t('page', { page: this.#page });
    }

    this.#setNavDisabled(prev, this.#page <= 1, this.#urlForPage(this.#page - 1));
    this.#setNavDisabled(next, !this.#hasNext, this.#urlForPage(this.#page + 1));
  }

  /**
   * @param {HTMLElement} el @param {boolean} disabled @param {URL} url
   */
  #setNavDisabled(el, disabled, url) {
    if (el instanceof HTMLButtonElement) {
      el.disabled = disabled;
      return;
    }
    if (disabled) {
      el.setAttribute('aria-disabled', 'true');
      el.setAttribute('tabindex', '-1');
      el.setAttribute('href', '#');
    } else {
      el.removeAttribute('aria-disabled');
      el.removeAttribute('tabindex');
      el.setAttribute('href', url.pathname + url.search);
    }
  }

  #updateClearButton() {
    if (this.refs.clear) this.refs.clear.hidden = !this.refs.input?.value;
  }

  /** @param {boolean} loading */
  #setLoading(loading) {
    if (loading) {
      this.#hide(this.refs.empty, this.refs.error);
      if (this.refs.grid.children.length > 0) {
        this.refs.grid.style.minHeight = `${this.refs.grid.offsetHeight}px`;
        this.classList.add('article-listing--loading');
        this.refs.loading.hidden = true;
        this.refs.grid.hidden = false;
      } else {
        this.refs.grid.hidden = true;
        this.refs.loading.hidden = false;
      }
    } else {
      this.classList.remove('article-listing--loading');
      this.refs.loading.hidden = true;
      this.refs.grid.hidden = false;
      this.refs.grid.style.minHeight = '';
    }
  }

  /** @param {string} term */
  #showEmpty(term) {
    this.#hideResults();
    this.refs.empty.textContent = term
      ? this.#t('noResultsQuery', { query: term })
      : this.#t('noResultsFilters');
    this.refs.empty.hidden = false;
  }

  #showError() {
    this.#hideResults();
    this.refs.error.hidden = false;
  }

  /** Hide the grid and any results navigation, leaving the status region for empty/error text. */
  #hideResults() {
    this.#hide(this.refs.loading, this.refs.grid, this.refs.loadMore, this.refs.pagination);
  }

  /** @param {...(HTMLElement | undefined)} elements */
  #hide(...elements) {
    for (const element of elements) if (element) element.hidden = true;
  }

  #seedSeenIds() {
    this.#seenIds = new Set(
      [...this.refs.grid.querySelectorAll('[data-article-id]')].map(
        (card) => /** @type {HTMLElement} */ (card).dataset.articleId ?? ''
      )
    );
  }

  /** @param {'type' | 'category'} facet @param {boolean} checked @param {HTMLInputElement} [except] */
  #setGroupChecked(facet, checked, except) {
    for (const input of this.#facetInputs) {
      if (input === except || input.dataset.all !== undefined) continue;
      if (input.dataset.facet === facet) input.checked = checked;
    }
  }
}

/**
 * @param {{ term?: string, types?: string[], categories?: string[], blogTitle?: string, lockedTag?: string }} params
 * @returns {string}
 */
export function buildSearchQuery({
  term = '',
  types = [],
  categories = [],
  blogTitle = '',
  lockedTag = '',
} = {}) {
  const parts = [];

  const safeTerm = term
    .replace(/[:"()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (safeTerm) parts.push(safeTerm);
  if (blogTitle) parts.push(`blog_title:${quote(blogTitle)}`);
  if (lockedTag) parts.push(`tag:${quote(lockedTag)}`);

  if (types.length) {
    const clause = types.map((t) => `tag:${quote(`type:${t}`)}`).join(' OR ');
    parts.push(types.length > 1 ? `(${clause})` : clause);
  }
  if (categories.length) {
    const clause = categories.map((c) => `tag:${quote(`category:${c}`)}`).join(' OR ');
    parts.push(categories.length > 1 ? `(${clause})` : clause);
  }

  return parts.join(' ');
}

/** @param {string} value */
function quote(value) {
  return `'${String(value).replace(/'/g, "\\'")}'`;
}

/**
 * Category label for a card: the anatta.article_type_label metafield, then a
 * category: tag, then a type: tag. Mirrors article-meta.liquid.
 * @param {any} node - Article node from the Storefront API.
 */
function categoryFromNode(node) {
  const label = node.articleTypeLabel?.value?.trim();
  if (label) return label;
  const tags = node.tags || [];
  const category = tags
    .find((t) => t.startsWith('category:'))
    ?.replace('category:', '')
    .trim();
  const type = tags
    .find((t) => t.startsWith('type:'))
    ?.replace('type:', '')
    .trim();
  return category || type || '';
}

/** @param {string | null | undefined} content — read time in minutes (≈200 wpm, floored, min 1, mirrors the Liquid card). */
function readTimeFromContent(content) {
  const words = content ? content.trim().split(/\s+/).filter(Boolean).length : 0;
  return Math.max(1, Math.floor(words / 200));
}

/** Strip HTML tags to plain text. @param {string} html */
function stripHtml(html) {
  return String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Truncate to at most `count` characters, adding an ellipsis (mirrors Liquid `truncate`). @param {string} text @param {number} count @param {string} [ellipsis] */
function truncateChars(text, count, ellipsis = '…') {
  if (text.length <= count) return text;
  return text.slice(0, Math.max(0, count - ellipsis.length)) + ellipsis;
}

/** @param {string} gid */
function numericId(gid) {
  return String(gid).split('/').pop() ?? gid;
}

/** Matches the `282 / 200` card ratio in `article-listing-card.liquid`, the taller of the two
 * breakpoint ratios, so `object-fit: cover` only ever crops width away. */
const CARD_IMAGE_RATIO = 200 / 282;

/** @param {string} url @param {number} width */
function sizedImage(url, width) {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('width', String(width));
    parsed.searchParams.set('height', String(Math.round(width * CARD_IMAGE_RATIO)));
    parsed.searchParams.set('crop', 'center');
    return parsed.toString();
  } catch {
    return url;
  }
}

/**
 * @template {keyof HTMLElementTagNameMap} T
 * @param {T} tag
 * @param {string} className
 * @param {string} [text]
 * @returns {HTMLElementTagNameMap[T]}
 */
function el(tag, className, text) {
  const node = document.createElement(tag);
  node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

const ARTICLES_QUERY = `
  query SearchArticles($query: String!, $first: Int!, $after: String) {
    articles(query: $query, first: $first, after: $after, sortKey: PUBLISHED_AT, reverse: true) {
      edges {
        node {
          id
          title
          handle
          excerpt
          tags
          content(truncateAt: 4000)
          image { url altText }
          blog { handle title }
          articleTypeLabel: metafield(namespace: "anatta", key: "article_type_label") { value }
        }
      }
      pageInfo { hasNextPage endCursor }
    }
  }
`;

const COUNT_QUERY = `
  query CountArticles($query: String!, $first: Int!, $after: String) {
    articles(query: $query, first: $first, after: $after, sortKey: PUBLISHED_AT, reverse: true) {
      nodes { id }
      pageInfo { hasNextPage endCursor }
    }
  }
`;

const TAGS_QUERY = `
  query FacetTags($query: String!, $first: Int!, $after: String) {
    articles(query: $query, first: $first, after: $after, sortKey: PUBLISHED_AT, reverse: true) {
      nodes { tags }
      pageInfo { hasNextPage endCursor }
    }
  }
`;

if (typeof customElements !== 'undefined' && !customElements.get('article-listing')) {
  customElements.define('article-listing', ArticleListing);
}
